import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngxs/store';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { GenericTreeEditorComponent } from '../generic-tree-editor/generic-tree-editor.component';
import { GenericTreeService, GENERIC_TREE_STATE_NAME } from '../services/generic-tree.entity.service';
import { MatDialog } from '@angular/material/dialog';
import { FormDialogGenericTreeComponent } from '../form-dialog-generic-tree/form-dialog-generic-tree.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AppSelectors } from '../../app.state';
import { EditorRole } from '../../editor/editor.role.enum';
import { toSignal } from '@angular/core/rxjs-interop';
import { GenericTreeEntity } from '../generic-tree.entity';
import { MetadataLevelAssignmentDialogComponent } from '../metadata-level-assignment-dialog/metadata-level-assignment-dialog.component';
import { GlobalMetaDataEntity } from '../../global-metadata/global-metadata.entity';
import { lastValueFrom } from 'rxjs';
import { GlobalMetaDataEntityService, GlobalMetaDataEntityState } from '../../global-metadata/services/global-metadata.entity.service';
import { GenericTreeHighlightService } from '../services/generic-tree-highlight.service';

@Component({
  selector: 'pixl-main-generic-tree',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    GenericTreeEditorComponent,
    TranslocoModule
  ],
  templateUrl: './main-generic-tree.component.html',
  styleUrl: './main-generic-tree.component.scss'
})
export class MainGenericTreeComponent {
  private store = inject(Store);
  private genericTreeService = inject(GenericTreeService);
  private globalMetadataService = inject(GlobalMetaDataEntityService);
  private dialog = inject(MatDialog);
  private translocoService = inject(TranslocoService);
  private highlightService = inject(GenericTreeHighlightService);

  private userRole = toSignal(this.store.select(AppSelectors.userRole));
  public isAdmin = computed(() => this.userRole() === EditorRole.AppAdmin || this.userRole() === EditorRole.ZAVAdmin);

  consumers$ = this.store.select(state => state.appState.consumers);
  subjects$ = this.store.select(state => state.appState.subjects);

  selectedConsumerId = signal<string | null>(null);
  selectedSubjectId = signal<string | null>(null);

  private treeState = toSignal(this.store.select(state => state[GENERIC_TREE_STATE_NAME]));

  public isAssignMetadataDisabled = computed(() => {
    const cid = this.selectedConsumerId();
    const sid = this.selectedSubjectId();

    if (!cid || !sid) {
      return true;
    }

    const state = this.treeState();
    if (!state || !state.entities) {
      return true;
    }

    const entities = Object.values(state.entities as Record<string, GenericTreeEntity>);
    const hasNodes = entities.some(node => node.consumerId === cid && node.subjectId === sid);

    return !hasNodes;
  });

  onConsumerChange(id: string): void {
    this.selectedConsumerId.set(id);
  }

  onSubjectChange(id: string): void {
    this.selectedSubjectId.set(id);
  }

  addRootNode(): void {
    const consumerId = this.selectedConsumerId();
    const subjectId = this.selectedSubjectId();

    if (!consumerId || !subjectId) {
      return;
    }

    const dialogRef = this.dialog.open(FormDialogGenericTreeComponent, {
      width: '500px',
      data: {
        title: this.translocoService.translate('genericTree.dialog.addRootNode'),
        genericTree: {
          consumerId,
          subjectId,
          root: true,
          active: true
        }
      }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        const state = this.store.selectSnapshot(s => s[GENERIC_TREE_STATE_NAME]);
        if (state && state.entities) {
          const entities = Object.values(state.entities as Record<string, GenericTreeEntity>)
            .filter(node => node.consumerId === consumerId && node.subjectId === subjectId);

          if (entities.length > 0) {
            const levelMap = this.genericTreeService.calculateNodeLevels(entities);
            const keys = this.genericTreeService.getMetadataKeysForLevel(entities, 1, levelMap);

            if (keys.length > 0) {
              result.consumerData = result.consumerData || {};
              keys.forEach(key => {
                if (result.consumerData[key] === undefined) {
                  result.consumerData[key] = null;
                }
              });
            }
          }
        }
        await this.genericTreeService.create(result);
      }
    });
  }

  async assignMetadataByLevel(): Promise<void> {
    const cid = this.selectedConsumerId();
    const sid = this.selectedSubjectId();

    if (!cid || !sid) {
      return;
    }

    // Load all metadata first to have the field definitions
    await this.globalMetadataService.ensureLoaded({ page: { size: 1000 } });
    const allMetadata = this.store.selectSnapshot(GlobalMetaDataEntityState.all);

    const allMetadataFields = new Set<string>();
    allMetadata.forEach(m => m.fieldConfig?.forEach(f => allMetadataFields.add(f.name)));

    const state = this.store.selectSnapshot(s => s[GENERIC_TREE_STATE_NAME]);
    const entities = Object.values(state.entities as Record<string, GenericTreeEntity>)
      .filter(node => node.consumerId === cid && node.subjectId === sid);

    if (entities.length === 0) {
      return;
    }

    const nodeLevelMap = this.genericTreeService.calculateNodeLevels(entities);
    const maxDepth = Math.max(...Array.from(nodeLevelMap.values()), 0);

    const firstNodes = this.genericTreeService.findFirstNodesPerLevel(entities);

    const activeMetadataByLevel: Record<number, string[]> = {};
    const levels = Array.from(new Set(nodeLevelMap.values()));

    levels.forEach(level => {
      const nodes = entities.filter(node => nodeLevelMap.get(node.id) === level);
      const activeIds = new Set<string>();

      allMetadata.forEach(meta => {
        // Active if AT LEAST ONE node has it
        const isPresent = nodes.some(node => {
          const data = node.consumerData || {};
          return meta.fieldConfig?.some(f => Object.prototype.hasOwnProperty.call(data, f.name));
        });
        if (isPresent) {
          activeIds.add(meta.id);
        }
      });
      activeMetadataByLevel[level] = Array.from(activeIds);
    });

    this.highlightService.setDimmed(true);
    this.highlightService.setTreeData(entities, nodeLevelMap, firstNodes);

    const dialogRef = this.dialog.open(MetadataLevelAssignmentDialogComponent, {
      width: '600px',
      data: {
        maxDepth,
        activeMetadataByLevel,
        saveFn: async (result: { level: number, metadata: GlobalMetaDataEntity[] }) => {
          const { level, metadata } = result;
          const nodesToUpdate = entities.filter(node => nodeLevelMap.get(node.id) === level);

          const selectedFieldNames = new Set<string>();
          metadata.forEach(m => m.fieldConfig?.forEach(f => selectedFieldNames.add(f.name)));

          for (const node of nodesToUpdate) {
            const currentData = node.consumerData || {};
            const newConsumerData: Record<string, unknown> = {};

            // Keep non-metadata fields and selected metadata fields
            Object.keys(currentData).forEach(key => {
              if (!allMetadataFields.has(key) || selectedFieldNames.has(key)) {
                newConsumerData[key] = currentData[key];
              }
            });

            // Ensure all selected fields exist
            selectedFieldNames.forEach(fieldName => {
              if (newConsumerData[fieldName] === undefined) {
                newConsumerData[fieldName] = null;
              }
            });

            await lastValueFrom(this.genericTreeService.update({
              ...node,
              consumerData: newConsumerData
            }));
          }
        }
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.highlightService.setDimmed(false);
      this.highlightService.setFocusedLevel(null);
      this.highlightService.clearTreeData();
    });
  }
}
